import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';

import { retrieveInventoryAssetDetail } from '@atom/actions/inventoryAssetActions';
import {
  cleanCreateInputs,
  getConfirmModalContent,
  getInitialCreateInputs,
} from '@atom/components/common/requests/customTenantUtilities';
import { CREATE_ASSET_REQUEST } from '@atom/graph/assetRequest';
import { Icon, IconButton, Modal, Snackbar } from '@atom/mui';
import {
  AssetRequest,
  AssetRequestCreateInput,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import RequestsContext from '../RequestsContext';

import CreateRequestModal from './CreateRequestModal';

const CreateRequest = () => {
  const dispatch = useDispatch();

  const { filters, asset, changeType, setActiveRequestId } =
    useContext(RequestsContext);

  const initialInputs = getInitialCreateInputs(filters?.type, asset);

  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [createInputs, setCreateInputs] =
    useState<AssetRequestCreateInput>(initialInputs);

  const [createRequest] = useMutation<
    { assetRequestCreate: AssetRequest },
    { input: AssetRequestCreateInput }
  >(CREATE_ASSET_REQUEST);

  const updateCreateInput = (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => {
    setCreateInputs(prev => ({ ...prev, [property]: value }));
  };

  const resetState = () => {
    setCreateInputs(initialInputs);
  };

  useEffect(() => {
    resetState();
  }, [filters]);

  const updateType = (type: AssetRequestType) => {
    setCreateInputs({ ...initialInputs, type });
  };

  const handleCreateClick = () => {
    setOpenCreate(false);
    setOpenConfirm(true);
  };

  const handleCreateClose = () => {
    setOpenCreate(false);
    resetState();
  };

  const handleConfirmClick = async () => {
    const input = cleanCreateInputs(createInputs);

    const { data } = await createRequest({
      variables: {
        input,
      },
    });

    Snackbar.info({
      message: 'Request created.',
    });

    setActiveRequestId(data?.assetRequestCreate?.id);
    changeType(createInputs.type);
    setOpenConfirm(false);
    resetState();

    if (createInputs.type === AssetRequestType.SDDOT_REPLENISHMENT) {
      dispatch(retrieveInventoryAssetDetail({ id: asset?.id }));
    }
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
    setOpenCreate(true);
  };

  const confirmModalContent = getConfirmModalContent(createInputs.type);
  const showCreateButton = hasRolePermissions(ROLE_SETS.INSPECTOR);

  return (
    <>
      {showCreateButton && (
        <IconButton
          edge="end"
          tooltip="Add Request"
          onClick={() => setOpenCreate(true)}
        >
          <Icon>add</Icon>
        </IconButton>
      )}
      <CreateRequestModal
        open={openCreate}
        onClose={handleCreateClose}
        createInputs={createInputs}
        updateCreateInput={updateCreateInput}
        onConfirm={handleCreateClick}
        updateType={updateType}
      />
      <Modal
        open={openConfirm}
        confirmButtonText="Create"
        onCancel={handleConfirmClose}
        onConfirm={handleConfirmClick}
        title={confirmModalContent.title}
      >
        {confirmModalContent.content}
      </Modal>
    </>
  );
};

export default CreateRequest;
